import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from '@mantine/form';
import { proposalApi } from '@modules/proposals/api';
import { GetGovernmentFeesRequestDTO } from '@modules/proposals/api/dto/getGovernmentFees';
import { EmirateType } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/components/MortgageDetails/utils/const';
import { TYPE_OF_TRANSACTION } from '@modules/opportunities/entities/opportunity/const';
import type { ProposalCreationClientAndMortgageForm } from '../presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/types';

type FeeCalculationParams = {
  form: ReturnType<typeof useForm<ProposalCreationClientAndMortgageForm>>;
  enabled?: boolean;
};

type FeesToDisplay = {
  real_estate: number;
  land_department: number;
};

export const useFeeCalculation = ({ form, enabled = true }: FeeCalculationParams) => {
  // Memoize form values to prevent unnecessary re-renders
  const formValues = useMemo(() => ({
    loanValue: form.values.mortgage_details.loan_value,
    citizenStatus: form.values.client_details.citizen_status,
    propertyState: form.values.property_details.state,
    propertyLocation: form.values.property_details.property_location,
    isLandDepartmentFeeFinanced: form.values.mortgage_details.is_land_department_fee_financed,
    isRealEstateFeeFinanced: form.values.mortgage_details.is_real_estate_fee_financed,
    mortgageType: form.values.mortgage_details.mortgage_type,
    propertyValue: form.values.mortgage_details.property_value,
    transactionType: form.values.mortgage_details.transaction_type,
  }), [
    form.values.mortgage_details.loan_value,
    form.values.client_details.citizen_status,
    form.values.property_details.state,
    form.values.property_details.property_location,
    form.values.mortgage_details.is_land_department_fee_financed,
    form.values.mortgage_details.is_real_estate_fee_financed,
    form.values.mortgage_details.mortgage_type,
    form.values.mortgage_details.property_value,
    form.values.mortgage_details.transaction_type
  ]);

  // Memoize conditions to prevent unnecessary re-calculations
  const conditions = useMemo(() => {
    const isFeeFinancingShown = formValues.transactionType === TYPE_OF_TRANSACTION.resale;
    const hasRequiredValues = !!(
      formValues.loanValue
      && formValues.citizenStatus
      && formValues.propertyState
      && formValues.mortgageType
      && formValues.propertyValue
      && formValues.transactionType
    );
    const hasFeeFinancingSelected = !!(
      formValues.isLandDepartmentFeeFinanced
      || formValues.isRealEstateFeeFinanced
    );

    return {
      isFeeFinancingShown,
      hasRequiredValues,
      hasFeeFinancingSelected,
      shouldFetch: isFeeFinancingShown && hasRequiredValues && hasFeeFinancingSelected,
    };
  }, [formValues]);

  // Memoize query parameters to enable request deduplication
  const queryParams = useMemo((): GetGovernmentFeesRequestDTO | null => {
    if (!conditions.shouldFetch) return null;

    return {
      citizenship_status: formValues.citizenStatus,
      is_land_department_fee_financed: formValues.isLandDepartmentFeeFinanced,
      is_real_estate_fee_financed: formValues.isRealEstateFeeFinanced,
      loan_amount: formValues.loanValue,
      mortgage_type: formValues.mortgageType,
      property_location: formValues.propertyLocation!,
      property_value: formValues.propertyValue,
      state: EmirateType[formValues.propertyState as keyof typeof EmirateType],
      type_of_transaction: formValues.transactionType,
    };
  }, [conditions.shouldFetch, formValues]);

  // Create a stable query key for request deduplication
  const queryKey = useMemo(() => [
    'governmentFees',
    queryParams
  ], [queryParams]);

  // Use React Query with modern patterns
  const query = useQuery({
    queryKey,
    queryFn: () => proposalApi.getGovernmentFees(queryParams!),
    enabled: enabled && conditions.shouldFetch && !!queryParams,
    staleTime: 5 * 60 * 1000, // 5 minutes - fees don't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (failureCount >= 2) return false;
      if (error && 'status' in error && typeof error.status === 'number') {
        return error.status >= 500; // Only retry server errors
      }
      return true;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Transform data to the expected format
  const feesToDisplay: FeesToDisplay = useMemo(() => {
    if (!query.data) {
      return { real_estate: 0, land_department: 0 };
    }

    return {
      land_department: query.data.land_department_transfer_fee.financed_fee_amount,
      real_estate: query.data.real_estate_fee.financed_fee_amount,
    };
  }, [query.data]);

  return {
    feesToDisplay,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
    conditions,
    rawData: query.data,
  };
};

export default useFeeCalculation;
